import { getAgeFromBirthDate } from 'core/service/insured/birthdate'
import _ from 'lodash'
import { constant, defaultTo, find, flow, get, isEqual, isUndefined, map } from 'lodash/fp'
import { getToggles } from 'quick-quote/feature-toggles'
import {
  getCurrentAppId,
  getInsuredMainOccupation,
  getInsuredOtherOccupation,
  isForeignerSelection,
} from '../../apps/selectors'
import { addPrefixToAmphur, addPrefixToTambol } from '../utils'
import { health } from './health-selector'
import { currentApp, currentQQ, fromBuddhistToChristian, when } from './primitive-selector'

const ADDR_TYPES = {
  CURRENT: 'currentAddress',
  OFFICE: 'officeAddress',
  REGISTERED: 'registeredAddress',
  FOREIGNER_SPOUSE_OFFICE: 'foreigner.spouse.officeAddress',
  FOREIGNER_SPOUSE_CURRENT: 'foreigner.spouse.address',
}

export const insured = (state) => {
  const title = currentApp('insured.title.text')(state)
  const titleValue = currentApp('insured.title.value')(state)
  const firstName = currentApp('insured.firstName')(state)
  const lastName = currentApp('insured.lastName')(state)
  const gender = currentQQ('insured.gender')(state)
  const idNo = currentApp('insured.idNo')(state)
  const idType = currentApp('insured.idType')(state)
  const nationality = currentApp('insured.nationality')(state)
  const maritalStatus = currentApp('insured.maritalStatus.value')(state)
  const age = flow(currentQQ('insured.age'), (ageObject) => {
    return ageObject.unit === 'year' ? `${ageObject.value}` : '0'
  })(state)
  const birthdate = flow(currentQQ('insured.birthdate.value'), fromBuddhistToChristian)(state)
  const mainOccupationIncomePerMonth = currentApp('insured.mainOccupationIncomePerMonth')(state)
  const otherOccupationIncomePerMonth = currentApp('insured.otherOccupationIncomePerMonth')(state)
  const healthInfo = health(currentApp('insured')(state))

  const hasOtherPolicy = currentApp('insured.hasOtherPolicy.value')(state)

  const educationText = currentApp('insured.education.text')(state)
  const educationValue = currentApp('insured.education.value')(state)
  const noOfDependence = currentApp('insured.noOfDependence.value')(state)

  const transformOtherPolicy = (policy) => {
    return {
      companyName: get('companyName')(policy),
      policyNumber: get('policyNumber')(policy),
      coverageType: get('coverageType.value')(policy),
      sumAssured: get('sumAssured')(policy),
      isCurrentlyEnforced: get('isCurrentlyEnforced.value')(policy),
    }
  }

  const getCurrentAddress = () => {
    const currentAddress = {
      addressNo: getAddressHouseNo(ADDR_TYPES.CURRENT),
      mhoo: getAddressMhoo(ADDR_TYPES.CURRENT),
      alley: getAddressAlley(ADDR_TYPES.CURRENT),
      road: getAddressRoad(ADDR_TYPES.CURRENT),
      numberMoobanArkan: getAddressNumberMoobanArkan(ADDR_TYPES.CURRENT),
      province: getAddressProvince(ADDR_TYPES.CURRENT),
      amphur: getAddressAmphur(ADDR_TYPES.CURRENT),
      tambol: getAddressTambol(ADDR_TYPES.CURRENT),
      postCode: getAddressPostCode(ADDR_TYPES.CURRENT),
    }

    return currentAddress
  }

  const getRegisteredAddress = () => {
    const registeredAddress = {
      addressNo: getAddressHouseNo(ADDR_TYPES.REGISTERED),
      mhoo: getAddressMhoo(ADDR_TYPES.REGISTERED),
      alley: getAddressAlley(ADDR_TYPES.REGISTERED),
      road: getAddressRoad(ADDR_TYPES.REGISTERED),
      numberMoobanArkan: getAddressNumberMoobanArkan(ADDR_TYPES.REGISTERED),
      province: getAddressProvince(ADDR_TYPES.REGISTERED),
      amphur: getAddressAmphur(ADDR_TYPES.REGISTERED),
      tambol: getAddressTambol(ADDR_TYPES.REGISTERED),
      postCode: getAddressPostCode(ADDR_TYPES.REGISTERED),
    }

    return registeredAddress
  }

  const getOfficeAddress = () => {
    const officeAddress = currentApp('insured.officeAddress.isSameAsCurrentAddress')(state)
      ? {
          name: officeName,
          workplaceName: officeAddressWorkplaceName,
          ...getCurrentAddress(),
        }
      : {
          name: officeName,
          workplaceName: officeAddressWorkplaceName,
          addressNo: getAddressHouseNo(ADDR_TYPES.OFFICE),
          mhoo: getAddressMhoo(ADDR_TYPES.OFFICE),
          alley: getAddressAlley(ADDR_TYPES.OFFICE),
          road: getAddressRoad(ADDR_TYPES.OFFICE),
          numberMoobanArkan: getAddressNumberMoobanArkan(ADDR_TYPES.OFFICE),
          province: getAddressProvince(ADDR_TYPES.OFFICE),
          amphur: getAddressAmphur(ADDR_TYPES.OFFICE),
          tambol: getAddressTambol(ADDR_TYPES.OFFICE),
          postCode: getAddressPostCode(ADDR_TYPES.OFFICE),
        }

    return officeAddress
  }

  const getSpouseCurrentAddress = () => {
    const currentSpouseAddressProvince = getAddressProvince(ADDR_TYPES.FOREIGNER_SPOUSE_CURRENT)
    const spouseCurrentAddress = {
      numberMoobanArkan: getAddressNumberMoobanArkan(ADDR_TYPES.FOREIGNER_SPOUSE_CURRENT),
      province: currentSpouseAddressProvince,
      amphur: getAddressAmphur(ADDR_TYPES.FOREIGNER_SPOUSE_CURRENT),
      tambol: getAddressTambol(ADDR_TYPES.FOREIGNER_SPOUSE_CURRENT),
      postCode: getAddressPostCode(ADDR_TYPES.FOREIGNER_SPOUSE_CURRENT),
    }

    return spouseCurrentAddress
  }

  const getSpouseOfficeAddress = () => {
    const currentSpouseOfficeAddressProvince = getAddressProvince(ADDR_TYPES.FOREIGNER_SPOUSE_OFFICE)
    const spouseUseCurrentInsuredAddress = currentApp('insured.foreigner.spouse.address.useCurrentInsuredAddress')(
      state
    )
    const spouseUseSameAsCurrentAddress = currentApp('insured.foreigner.spouse.officeAddress.isSameAsCurrentAddress')(
      state
    )
    const officeAddress = spouseUseSameAsCurrentAddress
      ? {
          ...getSpouseCurrentAddress(),
        }
      : {
          numberMoobanArkan: getAddressNumberMoobanArkan(ADDR_TYPES.FOREIGNER_SPOUSE_OFFICE),
          province: currentSpouseOfficeAddressProvince,
          amphur: getAddressAmphur(ADDR_TYPES.FOREIGNER_SPOUSE_OFFICE),
          tambol: getAddressTambol(ADDR_TYPES.FOREIGNER_SPOUSE_OFFICE),
          postCode: getAddressPostCode(ADDR_TYPES.FOREIGNER_SPOUSE_OFFICE),
        }
    const spouseOfficeAddress =
      spouseUseCurrentInsuredAddress && spouseUseSameAsCurrentAddress ? { ...getCurrentAddress() } : officeAddress
    return spouseOfficeAddress
  }

  const otherPolicy = flow(currentApp('insured.otherPolicy'), defaultTo([]), map(transformOtherPolicy))(state)

  const email = currentApp('insured.email')(state)
  const mobileNumber = currentApp('insured.mobileNumber')(state)
  const telephoneNumber = currentApp('insured.telephoneNumber')(state)
  const officeName = currentApp('insured.officeName')(state)
  const officeAddressWorkplaceName = currentApp('insured.officeAddress.workplaceName')(state)
  const getAddressNumberMoobanArkan = (addressType) => {
    return currentApp(`insured.${addressType}.numberMoobanArkan`)(state)
  }
  const getAddressProvince = (addressType) => {
    return currentApp(`insured.${addressType}.province`)(state)
  }
  const getAddressAmphur = (addressType) => {
    const province = getAddressProvince(addressType)
    return addPrefixToAmphur(currentApp(`insured.${addressType}.amphur.text`)(state), province)
  }
  const getAddressTambol = (addressType) => {
    const province = getAddressProvince(addressType)
    return addPrefixToTambol(currentApp(`insured.${addressType}.tambol.text`)(state), province)
  }
  const getAddressPostCode = (addressType) => {
    return currentApp(`insured.${addressType}.postCode.value`)(state)
  }
  const getAddressHouseNo = (addressType) => {
    return currentApp(`insured.${addressType}.addressNo`)(state)
  }
  const getAddressMhoo = (addressType) => {
    return currentApp(`insured.${addressType}.mhoo`)(state)
  }
  const getAddressAlley = (addressType) => {
    return currentApp(`insured.${addressType}.alley`)(state)
  }
  const getAddressRoad = (addressType) => {
    return currentApp(`insured.${addressType}.road`)(state)
  }

  ///
  const motorcycleUsage = flow(currentApp('insured.motorcycleUsage.value'), (value) => {
    if (isEqual(value, 'yes')) {
      return 'Y'
    } else if (isEqual(value, 'no')) {
      return 'N'
    } else {
      return value
    }
  })(state)
  const motorcycleUsageReason = currentApp('insured.motorcycleUsage.otherText')(state)

  const idKey = flow(
    find(idType.value),
    when(isUndefined, constant(''), get(idType.value))
  )([{ CITIZEN_ID: 'citizenId' }, { GOV_ID: 'citizenId' }, { PASSPORT: 'passport' }])
  const occupation = getInsuredMainOccupation(state)
  const otherOccupation = getInsuredOtherOccupation(state)
  const dividend = currentApp('insured.dividend')(state)
  const taxCertConsent = currentApp('insured.taxCertConsent')(state)
  const taxCertId = currentApp('insured.idNo.taxId')(state)

  const result = {
    id: idNo[idKey],
    idType,
    title,
    titleValue,
    firstName,
    lastName,
    nationality,
    educationText,
    educationValue,
    noOfDependence,
    gender,
    maritalStatus,
    age,
    birthdate,
    motorcycleUsage,
    motorcycleUsageReason,
    healthInfo,
    hasOtherPolicy,
    otherPolicy,
    dividend,
    contactInfo: {
      email,
      mobileNumber,
      telephoneNumber,
    },
    address: {
      current: getCurrentAddress(),
      registration: getRegisteredAddress(),
      office: getOfficeAddress(),
    },
    occupation: {
      occupationClass: _.get(occupation, 'occupationClass'),
      natureOfDutyCode: _.get(occupation, 'natureOfDutyCode'),
      incomePerMonth: mainOccupationIncomePerMonth,
      ..._.get(occupation, 'th'),
    },
    otherOccupation: {
      occupationClass: _.get(otherOccupation, 'occupationClass'),
      natureOfDutyCode: _.get(otherOccupation, 'natureOfDutyCode'),
      incomePerMonth: otherOccupationIncomePerMonth,
      ..._.get(otherOccupation, 'th'),
    },
    taxCertConsent,
    taxCertId,
  }
  if (getToggles().ENABLE_JUVENILE_PURCHASING_FORM) {
    result.juvenilePurchasing = currentApp('insured.juvenilePurchasing')(state)
  }
  if (getToggles().ENABLE_FOREIGNER_FORM && isForeignerSelection(state)) {
    const foreignerInfo = currentApp('insured.foreigner')(state)
    const spouseBirthdate = currentApp('insured.foreigner.spouse.birthdate')(state)

    const spouseAnnualIncome = currentApp('insured.foreigner.spouse.annualIncome')(state)
    const spouseOccupation = currentApp('insured.foreigner.spouse.occupation')(state)
    const spouseHowLongHaveYouBeenMarried = currentApp('insured.foreigner.spouse.howLongHaveYouBeenMarried')(state)
    const spouseKidData = currentApp('insured.foreigner.spouse.kid.dataChildren')(state)

    const spouseInfo = currentApp('insured.foreigner.spouse')(state)
    result.foreigner =
      foreignerInfo && spouseInfo
        ? Object.assign(foreignerInfo, {
            spouse: {
              annualIncome: spouseAnnualIncome,
              occupation: spouseOccupation,
              howLongHaveYouBeenMarried: spouseHowLongHaveYouBeenMarried,
              age: getAgeFromBirthDate(spouseBirthdate),
              address: getSpouseCurrentAddress(),
              officeAddress: getSpouseOfficeAddress(),
              kid: spouseKidData
                ? {
                    dataChildren: spouseKidData.map((kidData) => {
                      const kidBirthdate = kidData.birthdate
                      const kidAge = getAgeFromBirthDate(kidBirthdate)
                      return {
                        birthdate: kidBirthdate,
                        childrenAge: kidAge,
                      }
                    }),
                  }
                : null,
            },
          })
        : foreignerInfo
  }
  if (getToggles().ALLOW_AURA_QUESTIONARE) {
    result.auraSessionId = getCurrentAppId(state).split('_')[2]
  }
  if (getToggles().ENABLE_2_LINES_ADDRESS) {
    result.address.current.numberMoobanArkan2 = currentApp('insured.currentAddress.numberMoobanArkan2')(state)
    result.address.office.numberMoobanArkan2 = currentApp('insured.officeAddress.numberMoobanArkan2')(state)
    result.address.registration.numberMoobanArkan2 = currentApp('insured.registeredAddress.numberMoobanArkan2')(state)
  }
  if (getToggles().ENABLE_E_POLICY) {
    result.isPolicyDeliveryByEmail = currentApp('insured.isPolicyDeliveryByEmail.value')(state)
  }
  if (getToggles().ENABLE_TYPE_OF_BUSINESS) {
    result.occupation.typeOfBusiness = currentApp('insured.typeOfBusiness')(state)
  }

  const insured = currentApp('insured')(state)
  if (_.get(insured, 'CRS')) result.CRS = currentApp('insured.CRS')(state)
  if (_.get(insured, 'placeOfBirthCity')) result.placeOfBirthCity = currentApp('insured.placeOfBirthCity')(state)
  if (_.get(insured, 'placeOfBirthCountry'))
    result.placeOfBirthCountry = currentApp('insured.placeOfBirthCountry')(state)

  return result
}
